import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth/AuthProvider'
import { SignInScreen } from './auth/SignInScreen'
import { RegisterScreen } from './auth/RegisterScreen'
import { AdminKeysScreen } from './admin/AdminKeysScreen'
import { DownloadScreen } from './download/DownloadScreen'
import { LandingScreen } from './landing/LandingScreen'
import { AccountMenu } from './navigation/AccountMenu'
import { isAdminUid } from './admin/adminAllowlist'
import { LibraryView } from './library/LibraryView'
import { DataAdapterProvider } from './data/DataAdapterProvider'
import { UiPrimitivesProvider } from './ui'

function LoadingScreen() {
  return (
    <div className="grid min-h-dvh place-items-center text-text-tertiary">Loading…</div>
  )
}

function AuthedLibrary() {
  // LibraryView now renders AccountMenu inline in its own header, so
  // we don't stack a second floating one here. Previously the fixed
  // top-right pill overlapped the Settings button on phone viewports.
  return (
    <DataAdapterProvider>
      <UiPrimitivesProvider>
        <LibraryView />
      </UiPrimitivesProvider>
    </DataAdapterProvider>
  )
}

// Root route: landing page for anonymous visitors, the authenticated
// library for signed-in users. We deliberately do not put the sign-in
// form on the root route — the public face of govu.app should read like
// a product homepage, not a login screen.
function HomeRoute() {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  return user ? <AuthedLibrary /> : <LandingScreen />
}

function SignInRoute() {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  return user ? <Navigate to="/" replace /> : <SignInScreen />
}

function RegisterRoute() {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  return user ? <Navigate to="/" replace /> : <RegisterScreen />
}

// Lightweight wrapper that pins AccountMenu to the top-right of the
// viewport for routes that don't render their own header bar
// (DownloadScreen, AdminKeysScreen). LibraryView renders AccountMenu
// inline inside its own header and does NOT use this wrapper.
function FloatingAccountMenu() {
  return (
    <div className="fixed top-3 right-3 z-[9998]">
      <AccountMenu />
    </div>
  )
}

function AdminRoute() {
  const { user, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!user) return <Navigate to="/signin" replace />
  if (!isAdminUid(user.uid)) return <Navigate to="/" replace />
  return (
    <>
      <AdminKeysScreen />
      <FloatingAccountMenu />
    </>
  )
}

function DownloadRoute() {
  return (
    <>
      <DownloadScreen />
      <FloatingAccountMenu />
    </>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/signin" element={<SignInRoute />} />
          <Route path="/register" element={<RegisterRoute />} />
          <Route path="/download" element={<DownloadRoute />} />
          <Route path="/admin/keys" element={<AdminRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
